import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchPayments,
  UploadPaymentsCSV,
  FetchPaymentInvoice,
  DeletePayment,
  ProcessPayments,
  AddSinglePayment,
  ValidateSinglePayment,
  ProcessPayment,
  CancelPayment,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { Payment } from 'src/interfaces/payment'
import { PaymentStatus, SinglePaymentInvalidReason } from 'src/constants/payment/enums'
import { Pagination } from 'src/interfaces/table'
import { callSecureApi, ApiService } from '../api/callApi'

export interface FetchPaymentsPayload {
  partnerMarketId?: string
  search?: string
  limit?: number
  offset?: number
  sort?: string
  paymentStatus?: PaymentStatus
}

function* onFetchPayments(
  action: AsyncAction<FetchPaymentsPayload, { data: Array<Payment>; pagination: Pagination }>,
) {
  const { partnerMarketId, search, limit, offset, sort, paymentStatus } = action.payload

  try {
    const { payload }: { payload: { data: Array<Payment>; pagination: Pagination } } = yield call(
      callSecureApi,
      Endpoints.FetchPayments,
      {
        apiService: ApiService.Paraworks,
        query: {
          partnerMarketId,
          search,
          limit,
          offset,
          sort,
          paymentStatus,
        },
      },
    )

    yield put(FetchPayments.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch payments'

    yield put(FetchPayments.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadPaymentsCSVRequest {
  file: File
  partnerId: string
}

function* onUploadPaymentsCSV(action: AsyncAction<UploadPaymentsCSVRequest>) {
  const { file } = action.payload

  try {
    const formData = new FormData()

    formData.append('file', file)

    yield call(callSecureApi, Endpoints.UploadPaymentsCSV, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      // returnResponse: true,
    })

    yield put(UploadPaymentsCSV.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload payments csv'

    yield put(UploadPaymentsCSV.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface FetchPaymentInvoicePayload {
  paymentId: string
}

function* onFetchPaymentInvoice(action: AsyncAction<FetchPaymentInvoicePayload, Array<Payment>>) {
  const { paymentId } = action.payload

  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.FetchPaymentInvoice,
      {
        apiService: ApiService.Paraworks,
        params: [
          {
            value: paymentId,
            field: ':paymentId',
          },
        ],
        returnBuffer: true,
      },
    )

    yield put(FetchPaymentInvoice.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch payments'

    yield put(FetchPaymentInvoice.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface DeletePaymentPayload {
  paymentIds: Array<string>
}

function* onDeletePayment(action: AsyncAction<DeletePaymentPayload, Array<Payment>>) {
  const { paymentIds } = action.payload

  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.DeletePayment,
      {
        method: 'DELETE',
        apiService: ApiService.Paraworks,
        body: {
          earningIds: paymentIds,
        },
      },
    )

    yield put(DeletePayment.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to delete payment'

    yield put(DeletePayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface CancelPaymentPayload {
  paymentId: string
}

function* onCancelPayment(action: AsyncAction<CancelPaymentPayload, Array<Payment>>) {
  const { paymentId } = action.payload

  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.CancelPayment,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [
          {
            value: paymentId,
            field: ':paymentId',
          },
        ],
      },
    )

    yield put(CancelPayment.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to cancel payment'

    yield put(CancelPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onProcessPayments(action: AsyncAction<void, Array<Payment>>) {
  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.ProcessPayments,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
      },
    )

    yield put(ProcessPayments.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to process payments'

    yield put(ProcessPayments.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface ProcessPaymentPayload {
  paymentId: string
}

function* onProcessPayment(action: AsyncAction<ProcessPaymentPayload, Array<Payment>>) {
  const { paymentId } = action.payload

  try {
    const { payload }: { payload: Array<Payment> } = yield call(
      callSecureApi,
      Endpoints.ProcessPayment,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [
          {
            value: paymentId,
            field: ':paymentId',
          },
        ],
      },
    )

    yield put(ProcessPayment.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to process payment'

    yield put(ProcessPayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ValidateSinglePaymentRequest {
  partnerDriverId: string
  amount: number
}

export interface ValidateSinglePaymentResponse {
  isValid: boolean
  reason?: SinglePaymentInvalidReason
}

function* onValidateSinglePayment(
  action: AsyncAction<ValidateSinglePaymentRequest, ValidateSinglePaymentResponse>,
) {
  const { partnerDriverId, amount } = action.payload

  try {
    const { payload }: { payload: ValidateSinglePaymentResponse } = yield call(
      callSecureApi,
      Endpoints.ValidateSinglePayment,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: {
          partnerDriverId,
          amount,
        },
      },
    )

    yield put(ValidateSinglePayment.success())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to validate payment'

    yield put(ValidateSinglePayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AddSinglePaymentRequest {
  partnerDriverId: string
  amount: number
}

function* onAddSinglePayment(action: AsyncAction<AddSinglePaymentRequest>) {
  const { partnerDriverId, amount } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AddSinglePayment, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverId,
        amount,
      },
    })

    yield put(AddSinglePayment.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to create payment'

    yield put(AddSinglePayment.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* paymentSharedSaga() {
  yield takeEvery(FetchPayments.type.REQUEST, onFetchPayments)
  yield takeEvery(UploadPaymentsCSV.type.REQUEST, onUploadPaymentsCSV)
  yield takeEvery(FetchPaymentInvoice.type.REQUEST, onFetchPaymentInvoice)
  yield takeEvery(DeletePayment.type.REQUEST, onDeletePayment)
  yield takeEvery(CancelPayment.type.REQUEST, onCancelPayment)
  yield takeEvery(ProcessPayments.type.REQUEST, onProcessPayments)
  yield takeEvery(ProcessPayment.type.REQUEST, onProcessPayment)
  yield takeEvery(ValidateSinglePayment.type.REQUEST, onValidateSinglePayment)
  yield takeEvery(AddSinglePayment.type.REQUEST, onAddSinglePayment)
}
